import { useState, useEffect } from 'react'
import { WhichWeightAPI } from 'WhichWeightAPI'
import { ArrowRightLeftIcon } from 'lucide-react'
import { MinusCircleIcon, PlusCircleIcon } from '@heroicons/react/24/outline'
import moment from 'moment'

import SolidButton from 'components/Buttons/SolidButton'
import BrandColorButton from 'components/Buttons/BrandColorButton'
import SearchBar from 'components/Search/SearchBar'
import ContentFrame from 'components/Frame/ContentFrame'
import AthleteRosterManagementListItem from 'components/Group/AthleteRosterManagementListItem'

export default function GroupAthletesTabEditMode({
    weekOfTheYearKey,
    organizationId,
    coaches=[],
    athletes=[],
    groupId,
    groupAthletes=[],
    OnSaveSuccess = () => {},
    setViewMode = () => {}
}) {

    const [pendingGroupAthletes, setPendingGroupAthletes] = useState(null)
    const [availableOrganizationAthletes, setAvailableOrganizationAthletes] = useState(null)
    const [groupSearchTerm, setGroupSearchTerm] = useState('')
    const [organizationSearchTerm, setOrganizationSearchTerm] = useState('')
    const [saveLoading, setSaveLoading] = useState(false)

    const InitializeAthleteLists = () => {

        const initialPending = groupAthletes ? JSON.parse(JSON.stringify(groupAthletes)) : []

        const initialAvailable = (athletes || []).filter(
            orgAthlete => !initialPending.some(groupAthlete => groupAthlete.id === orgAthlete.id)
        )

        setPendingGroupAthletes(initialPending)
        setAvailableOrganizationAthletes(initialAvailable)
    }

    const HandleRemoveAthleteFromGroup = (athleteId) => {
        setPendingGroupAthletes((prevPending) => {
            const newPending = prevPending.filter(athlete => athlete.id !== athleteId)

            const newAvailable = (athletes || []).filter(
                orgAthlete => !newPending.some(groupAthlete => groupAthlete.id === orgAthlete.id)
            )

            setAvailableOrganizationAthletes(newAvailable)

            return newPending
        })
    }

    const HandleAddAthleteToGroup = (athleteId) => {
        if (!athletes) return

        const athleteToAdd = athletes.find(athlete => athlete.id === athleteId)

        if (!athleteToAdd) return

        setPendingGroupAthletes((prevPending) => {
            // If already in the group, just recompute available list
            if (prevPending.some(athlete => athlete.id === athleteId)) {
                const available = (athletes || []).filter(
                    orgAthlete => !prevPending.some(groupAthlete => groupAthlete.id === orgAthlete.id)
                )
                setAvailableOrganizationAthletes(available)
                return prevPending
            }

            const newPending = [...prevPending, athleteToAdd]

            const newAvailable = (athletes || []).filter(
                orgAthlete => !newPending.some(groupAthlete => groupAthlete.id === orgAthlete.id)
            )

            setAvailableOrganizationAthletes(newAvailable)

            return newPending
        })
    }

    const FilterAthletesBySearchTerm = (item, searchTerm) => {
        if (!searchTerm || searchTerm.trim().length === 0) return true

        const fullName = `${item.firstName} ${item.lastName}`.toLowerCase()
        const email = (item.email || '').toLowerCase()
        const term = searchTerm.toLowerCase()

        return fullName.includes(term) || email.includes(term)
    }

    const filteredPendingGroupAthletes = (pendingGroupAthletes || []).filter(item => FilterAthletesBySearchTerm(item, groupSearchTerm))
    const filteredAvailableOrganizationAthletes = (availableOrganizationAthletes || []).filter(item => FilterAthletesBySearchTerm(item, organizationSearchTerm))

    const OnSaveChanges = async () => {
        setSaveLoading(true)

        const jsonBody = {
            organizationId: organizationId, // used for authorization
            groupId: groupId,
            athleteIds: pendingGroupAthletes.map(athlete => athlete.id),
            weekOfTheYearKey: weekOfTheYearKey,
            clientDate: moment().local().format('YYYY-MM-DD') // V5 groups: the week whose workouts are given to added athletes and cleared for removed ones
        }

        // console.log(jsonBody)

        const result = await WhichWeightAPI('REPORTING', 'POST', '/group_athletes', jsonBody)

        // console.log(result)

        if (result.data.status === 'failure') {
            console.error(result)
            setSaveLoading(false)
            return
        }

        setSaveLoading(false)

        if (OnSaveSuccess) {
            OnSaveSuccess()
        }

        setViewMode('view-group-athletes')
    }
    
    useEffect(() => {
        if (groupAthletes !== null && athletes !== null) {
            InitializeAthleteLists()
        }
    }, [groupAthletes, athletes])

    if (athletes === null || coaches === null || pendingGroupAthletes === null || availableOrganizationAthletes === null) {
        return (
            <div className='space-y-4 p-4'>
                <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
            </div>
        )
    }

    return (
        <div className="h-full flex flex-col min-h-0">
            <div className="flex-shrink-0 flex items-center gap-x-4 mb-4">
                <div className="h-[42px] flex-grow flex items-center">Add or Remove Athletes in the Group</div>

                <div className="w-32">
                    <SolidButton
                        size="small"
                        text="Cancel"
                        action={() => setViewMode('view-group-athletes')}
                        className="shadow-md"
                    />
                </div>

                <div className="w-32">
                    <BrandColorButton
                        size="small"
                        text="Save"
                        action={() => OnSaveChanges()}
                        isLoading={saveLoading}
                        className="shadow-md"
                    />
                </div>
            </div>

            <div className='flex-1 min-h-0 flex gap-x-6'>

                {/* Remove List */}

                <div className='flex-1 min-h-0 flex flex-col gap-y-2'>
                    <div className='text-sm font-semibold text-gray-500'>Group Athletes</div>

                    <SearchBar
                        placeholder="Enter Athlete Name or Email"
                        searchTerm={groupSearchTerm}
                        setSearchTerm={setGroupSearchTerm}
                        showFiltersToggle={false}
                    />

                    <ContentFrame className='flex-1 min-h-0 !items-stretch !justify-start'>
                        <div className='w-full h-full divide-y divide-gray-200 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]'>
                            {filteredPendingGroupAthletes.length === 0 &&
                                <div className="text-center text-gray-500 py-4">No athletes found</div>
                            }

                            {filteredPendingGroupAthletes.length > 0 && filteredPendingGroupAthletes.map((athlete) => (
                                <AthleteRosterManagementListItem
                                    key={athlete.id}
                                    athlete={athlete}
                                    coaches={coaches}
                                    groupId={groupId}
                                    actionTag='remove'
                                    actionIcon={<MinusCircleIcon className="text-red-500 h-8" />}
                                    OnAthletePressed={() => HandleRemoveAthleteFromGroup(athlete.id)}
                                />
                            ))}
                        </div>
                    </ContentFrame>
                </div>

                <ArrowRightLeftIcon className="my-auto h-10 w-10 text-gray-400 flex-shrink-0" />

                {/* Add List */}

                <div className='flex-1 min-h-0 flex flex-col gap-y-2'>
                    <div className='text-sm font-semibold text-gray-500'>Organization Athletes</div>

                    <SearchBar
                        placeholder="Enter Athlete Name or Email"
                        searchTerm={organizationSearchTerm}
                        setSearchTerm={setOrganizationSearchTerm}
                        showFiltersToggle={false}
                    />

                    <ContentFrame className='flex-1 min-h-0 !items-stretch !justify-start'>
                        <div className='w-full h-full divide-y divide-gray-200 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]'>
                            {filteredAvailableOrganizationAthletes.length === 0 &&
                                <div className="text-center text-gray-500 py-4">No athletes found</div>
                            }

                            {filteredAvailableOrganizationAthletes.length > 0 && filteredAvailableOrganizationAthletes.map((athlete) => (
                                <AthleteRosterManagementListItem
                                    key={athlete.id}
                                    athlete={athlete}
                                    coaches={coaches}
                                    groupId={groupId}
                                    actionTag='add'
                                    actionIcon={<PlusCircleIcon className="text-green-500 h-8" />}
                                    OnAthletePressed={() => HandleAddAthleteToGroup(athlete.id)}
                                />
                            ))}
                        </div>
                    </ContentFrame>
                </div>
            </div>
        </div>
    )
}
